import api from "../services/api";
import { BUNDLE_VERSION } from "./bundleVersion";

/**
 * Telemetria de erro do frontend → POST /api/client-errors (só logado).
 * Dedup por source+message numa janela, teto por sessão, lote a cada 5 s,
 * flush no pagehide via fetch keepalive. Sem PII: nunca corpo de mensagem,
 * nunca dataJson — só message/stack/componentStack/pathname/UA/bundleVersion.
 */

export const CLIENT_ERRORS_PATH = "/api/client-errors";

/** Teto do backend (`CLIENT_ERROR_LIMITS.batch`); flush e keepalive fatiam neste tamanho. */
export const CLIENT_ERRORS_BATCH = 5;

export const IGNORED_MESSAGE_PATTERNS = Object.freeze([
  /ResizeObserver loop/i,
  /^Script error\.?$/i,
  /Loading chunk [\w-]+ failed/i, // AppErrorBoundary já recarrega; não vale reportar
  /^canceled$/i, // Axios CanceledError (navegação/abort deliberado)
]);

/**
 * Falha de transporte (axios ERR_NETWORK, fetch). Offline puro é descartado;
 * online, vai no máximo 1 por sessão com `online: true` — é o único sinal
 * client-side de CORS quebrado / 502 sem headers em UM endpoint após deploy.
 */
export const NETWORK_MESSAGE_PATTERNS = Object.freeze([
  /^Network Error$/i,
  /^Failed to fetch$/i,
  /^Load failed$/i, // Safari
]);

export const NETWORK_ERRORS_PER_SESSION = 1;

const LIMITS = Object.freeze({ message: 500, stack: 2000, componentStack: 2000 });

function toMessage(error) {
  if (error instanceof Error) return error.message || error.name || "Error";
  if (typeof error === "string") return error;
  if (error && typeof error === "object") {
    if (typeof error.message === "string") return error.message;
    const keys = Object.keys(error).slice(0, 5).join(",");
    return `Non-Error rejection: ${error.constructor?.name || "Object"}{${keys}}`;
  }
  return String(error);
}

function cut(value, max) {
  if (typeof value !== "string" || !value) return undefined;
  return value.length > max ? value.slice(0, max) : value;
}

function readStoredToken() {
  try {
    const raw = window.localStorage.getItem("token");
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return raw;
    }
  } catch (e) {
    return null;
  }
}

export function createClientErrorReporter({
  send,
  now = () => Date.now(),
  flushIntervalMs = 5000,
  maxPerSession = 20,
  dedupWindowMs = 60_000,
  getToken = readStoredToken,
  bundleVersion = BUNDLE_VERSION,
} = {}) {
  const queue = [];
  const lastSeen = new Map(); // `${source}:${message}` → timestamp
  let sentOrQueued = 0;
  let networkReported = 0;
  let timer = null;
  let disposed = false;

  const isOffline = () => {
    try {
      return typeof navigator !== "undefined" && navigator.onLine === false;
    } catch (e) {
      return false;
    }
  };

  const schedule = () => {
    if (timer || disposed) return;
    timer = setTimeout(() => {
      timer = null;
      flush();
    }, flushIntervalMs);
  };

  async function flush() {
    if (queue.length === 0) return;
    const batch = queue.splice(0, queue.length);
    for (let i = 0; i < batch.length; i += CLIENT_ERRORS_BATCH) {
      try {
        await send(batch.slice(i, i + CLIENT_ERRORS_BATCH));
      } catch (e) {
        // best-effort: telemetria nunca pode virar outro erro
      }
    }
  }

  function report({ error, source, componentStack, messageId } = {}) {
    if (disposed) return false;
    if (!getToken()) return false;
    const message = cut(toMessage(error), LIMITS.message);
    if (!message) return false;
    if (IGNORED_MESSAGE_PATTERNS.some((re) => re.test(message))) return false;
    const isNetwork = NETWORK_MESSAGE_PATTERNS.some((re) => re.test(message));
    if (isNetwork) {
      if (isOffline()) return false;
      if (networkReported >= NETWORK_ERRORS_PER_SESSION) return false;
    }
    if (sentOrQueued >= maxPerSession) return false;

    const key = `${source}:${message}`;
    const t = now();
    const prev = lastSeen.get(key);
    if (prev !== undefined && t - prev < dedupWindowMs) return false;
    lastSeen.set(key, t);

    let pathname;
    try {
      pathname = window.location.pathname;
    } catch (e) {
      pathname = undefined;
    }

    queue.push({
      message,
      source,
      stack: cut(error instanceof Error ? error.stack : undefined, LIMITS.stack),
      componentStack: cut(componentStack, LIMITS.componentStack),
      pathname,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      bundleVersion: bundleVersion || undefined,
      messageId: typeof messageId === "number" ? messageId : undefined,
      at: new Date(t).toISOString(),
    });
    sentOrQueued += 1;
    if (isNetwork) networkReported += 1;
    schedule();
    return true;
  }

  return {
    report,
    flush,
    drain: () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      return queue.splice(0, queue.length);
    },
    size: () => queue.length,
    dispose: () => {
      disposed = true;
      if (timer) clearTimeout(timer);
      timer = null;
    },
  };
}

// ---- singleton usado pela app ---------------------------------------------

export async function sendViaApi(batch) {
  await api.post(CLIENT_ERRORS_PATH, { errors: batch });
}

// No pagehide o axios pode não completar; fetch keepalive com o mesmo Bearer.
export function sendKeepalive(batch) {
  try {
    const token = readStoredToken();
    if (!token || typeof fetch !== "function") return;
    const origin = String(api.defaults.baseURL || "").replace(/\/+$/, "");
    const list = Array.isArray(batch) ? batch : [];
    for (let i = 0; i < list.length; i += CLIENT_ERRORS_BATCH) {
      fetch(`${origin}${CLIENT_ERRORS_PATH}`, {
        method: "POST",
        keepalive: true,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ errors: list.slice(i, i + CLIENT_ERRORS_BATCH) }),
      }).catch(() => {});
    }
  } catch (e) {
    /* ignore */
  }
}

let singleton = null;
function getSingleton() {
  if (!singleton) singleton = createClientErrorReporter({ send: sendViaApi });
  return singleton;
}

export function reportClientError(input) {
  try {
    return getSingleton().report(input);
  } catch (e) {
    return false;
  }
}

let installed = false;
export function installGlobalErrorHandlers() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (event) => {
    reportClientError({ error: event?.error || event?.message, source: "window" });
  });

  window.addEventListener("unhandledrejection", (event) => {
    reportClientError({ error: event?.reason, source: "unhandledrejection" });
  });

  // Descarrega a fila antes de a aba sumir (sem esperar o timer de 5 s).
  window.addEventListener("pagehide", () => {
    const reporter = getSingleton();
    const pending = reporter.drain();
    if (pending.length) sendKeepalive(pending);
  });
}
